/**
 * Task Hooks
 * React Query hooks for task management
 *
 * NOTE: The user_tasks table exists in database.types.ts but may not be in
 * database.generated.ts. Using type assertions similar to use-diagnostic.ts.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import type {
  UserTask,
  TaskStatus,
  TaskType,
  CreateTaskInput,
  TaskMetadata,
} from '@/types/task';
import { DISMISS_RESURFACE_DAYS } from '@/types/task';
import { differenceInDays, parseISO } from 'date-fns';
import {
  emitSignal,
  createTaskCompletedSignal,
  createDiagnosticCompletedSignal,
  createActivitySkippedSignal,
  createSyllabusTasksGeneratedSignal,
} from '@/lib/signals';
import { normalizeSubject } from '@/lib/subject-mapping';

// ============================================================================
// DATABASE ROW TYPE
// ============================================================================

interface UserTaskRow {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  task_type: string;
  status: string;
  priority: number;
  is_starred: boolean;
  due_date: string | null;
  scheduled_date: string | null;
  completed_at: string | null;
  completion_notes: string | null;
  estimated_minutes: number | null;
  actual_minutes: number | null;
  related_subject: string | null;
  related_topic: string | null;
  study_plan_id: string | null;
  syllabus_id: string | null;
  source: string | null;
  resource_links: unknown | null;
  metadata: unknown | null;
  created_at: string | null;
  updated_at: string | null;
}

// Helper to convert database row to UserTask
function toTask(row: UserTaskRow): UserTask {
  return {
    id: row.id,
    user_id: row.user_id,
    title: row.title,
    description: row.description,
    task_type: row.task_type as TaskType,
    status: row.status as TaskStatus,
    priority: row.priority,
    is_starred: row.is_starred ?? false,
    due_date: row.due_date,
    scheduled_date: row.scheduled_date,
    completed_at: row.completed_at,
    completion_notes: row.completion_notes,
    estimated_minutes: row.estimated_minutes,
    actual_minutes: row.actual_minutes,
    related_subject: row.related_subject,
    related_topic: row.related_topic,
    study_plan_id: row.study_plan_id,
    syllabus_id: row.syllabus_id,
    source: row.source,
    resource_links: row.resource_links as string[] | null,
    metadata: row.metadata as TaskMetadata | null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const taskKeys = {
  all: ['tasks'] as const,
  active: (userId: string) => [...taskKeys.all, 'active', userId] as const,
  completed: (userId: string, limit?: number) =>
    [...taskKeys.all, 'completed', userId, limit] as const,
  byType: (userId: string, type: TaskType) =>
    [...taskKeys.all, 'type', userId, type] as const,
  bySubject: (userId: string, subject: string) =>
    [...taskKeys.all, 'subject', userId, subject] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch active (pending/in_progress) tasks for the current user
 * Also resurfaces dismissed tasks after DISMISS_RESURFACE_DAYS
 */
export function useActiveTasks() {
  const { user } = useAuth();

  return useQuery({
    queryKey: taskKeys.active(user?.id ?? ''),
    queryFn: async (): Promise<UserTask[]> => {
      if (!user?.id) return [];

      // Fetch active tasks (pending/in_progress)
      const { data: activeTasks, error: activeError } = await (supabase
        .from('user_tasks' as any)
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['pending', 'in_progress'])
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false }) as unknown as Promise<{
        data: UserTaskRow[] | null;
        error: any;
      }>);

      if (activeError) throw activeError;

      // Calculate the cutoff date for resurfacing dismissed tasks
      const resurfaceCutoff = new Date();
      resurfaceCutoff.setDate(resurfaceCutoff.getDate() - DISMISS_RESURFACE_DAYS);

      // Fetch dismissed tasks that should resurface (dismissed before cutoff)
      const { data: dismissedTasks, error: dismissedError } = await (supabase
        .from('user_tasks' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'dismissed')
        .lt('updated_at', resurfaceCutoff.toISOString())
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false }) as unknown as Promise<{
        data: UserTaskRow[] | null;
        error: any;
      }>);

      if (dismissedError) throw dismissedError;

      // Resurface dismissed tasks by resetting them to pending
      const resurfacedTasks: UserTaskRow[] = [];
      if (dismissedTasks && dismissedTasks.length > 0) {
        for (const task of dismissedTasks) {
          const { data: updatedTask, error: updateError } = await (supabase
            .from('user_tasks' as any)
            .update({ status: 'pending', updated_at: new Date().toISOString() })
            .eq('id', task.id)
            .eq('user_id', user.id)
            .select()
            .single() as unknown as Promise<{ data: UserTaskRow; error: any }>);

          if (!updateError && updatedTask) {
            resurfacedTasks.push(updatedTask);
          }
        }
      }

      // Combine active and resurfaced tasks
      const allTasks = [...(activeTasks ?? []), ...resurfacedTasks];

      // Sort by priority (descending) then created_at (descending)
      allTasks.sort((a, b) => {
        if (b.priority !== a.priority) return b.priority - a.priority;
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });

      return allTasks.map(toTask);
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch recently completed tasks
 */
export function useCompletedTasks(limit = 5) {
  const { user } = useAuth();

  return useQuery({
    queryKey: taskKeys.completed(user?.id ?? '', limit),
    queryFn: async (): Promise<UserTask[]> => {
      if (!user?.id) return [];

      const { data, error } = await (supabase
        .from('user_tasks' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(limit) as unknown as Promise<{
        data: UserTaskRow[] | null;
        error: any;
      }>);

      if (error) throw error;
      return (data ?? []).map(toTask);
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch tasks by type
 */
export function useTasksByType(taskType: TaskType) {
  const { user } = useAuth();

  return useQuery({
    queryKey: taskKeys.byType(user?.id ?? '', taskType),
    queryFn: async (): Promise<UserTask[]> => {
      if (!user?.id) return [];

      const { data, error } = await (supabase
        .from('user_tasks' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('task_type', taskType)
        .neq('status', 'completed')
        .order('priority', { ascending: false }) as unknown as Promise<{
        data: UserTaskRow[] | null;
        error: any;
      }>);

      if (error) throw error;
      return (data ?? []).map(toTask);
    },
    enabled: !!user?.id && !!taskType,
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new task
 */
export function useCreateTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateTaskInput): Promise<UserTask> => {
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await (supabase
        .from('user_tasks' as any)
        .insert({
          user_id: user.id,
          title: input.title,
          description: input.description || null,
          task_type: input.task_type,
          status: 'pending',
          priority: input.priority ?? 1,
          due_date: input.due_date || null,
          scheduled_date: input.scheduled_date || null,
          estimated_minutes: input.estimated_minutes || null,
          related_subject: input.related_subject || null,
          related_topic: input.related_topic || null,
          study_plan_id: input.study_plan_id || null,
          syllabus_id: input.syllabus_id || null,
          source: input.source || 'system',
          resource_links: input.resource_links || null,
          metadata: input.metadata || null,
        })
        .select()
        .single() as unknown as Promise<{ data: UserTaskRow; error: any }>);

      if (error) throw error;
      return toTask(data);
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: taskKeys.active(user.id) });
      }
    },
  });
}

/**
 * Update task fields (for editing)
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      taskId,
      updates,
    }: {
      taskId: string;
      updates: Partial<Omit<CreateTaskInput, 'source'>>;
    }): Promise<UserTask> => {
      if (!user?.id) throw new Error('Not authenticated');

      const updateData: Record<string, unknown> = {};

      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.description !== undefined) updateData.description = updates.description || null;
      if (updates.task_type !== undefined) updateData.task_type = updates.task_type;
      if (updates.priority !== undefined) updateData.priority = updates.priority;
      if (updates.due_date !== undefined) updateData.due_date = updates.due_date || null;
      if (updates.scheduled_date !== undefined) updateData.scheduled_date = updates.scheduled_date || null;
      if (updates.estimated_minutes !== undefined) updateData.estimated_minutes = updates.estimated_minutes || null;
      if (updates.related_subject !== undefined) updateData.related_subject = updates.related_subject || null;
      if (updates.related_topic !== undefined) updateData.related_topic = updates.related_topic || null;
      if (updates.metadata !== undefined) updateData.metadata = updates.metadata || null;

      // SECURITY: Always include user_id check to prevent updating other users' tasks
      const { data, error } = await (supabase
        .from('user_tasks' as any)
        .update(updateData)
        .eq('id', taskId)
        .eq('user_id', user.id)
        .select()
        .single() as unknown as Promise<{ data: UserTaskRow; error: any }>);

      if (error) throw error;
      return toTask(data);
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: taskKeys.active(user.id) });
        // Use partial key to match all completed queries regardless of limit
        queryClient.invalidateQueries({ queryKey: ['tasks', 'completed', user.id] });
      }
    },
  });
}

/**
 * Update task status
 */
export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      taskId,
      status,
      completionNotes,
      actualMinutes,
    }: {
      taskId: string;
      status: TaskStatus;
      completionNotes?: string;
      actualMinutes?: number;
    }): Promise<UserTask> => {
      const updateData: Record<string, unknown> = { status };

      if (status === 'completed') {
        updateData.completed_at = new Date().toISOString();
        if (completionNotes) updateData.completion_notes = completionNotes;
        if (actualMinutes !== undefined) updateData.actual_minutes = actualMinutes;
      } else if (status === 'pending' || status === 'in_progress') {
        // Clear completion fields when restoring a task to active status
        updateData.completed_at = null;
        updateData.completion_notes = null;
        updateData.actual_minutes = null;
      }

      if (!user?.id) throw new Error('Not authenticated');

      // SECURITY: Always include user_id check to prevent updating other users' tasks
      const { data, error } = await (supabase
        .from('user_tasks' as any)
        .update(updateData)
        .eq('id', taskId)
        .eq('user_id', user.id)
        .select()
        .single() as unknown as Promise<{ data: UserTaskRow; error: any }>);

      if (error) throw error;

      const task = toTask(data);

      // Emit learning signals for status changes
      if (user?.id && (status === 'completed' || status === 'dismissed')) {
        try {
          if (status === 'completed') {
            // Emit appropriate signal based on task type
            if (task.task_type === 'diagnostic') {
              const signal = createDiagnosticCompletedSignal(user.id, {
                task_id: task.id,
                subject: task.related_subject || 'unknown',
                time_spent_seconds: actualMinutes ? actualMinutes * 60 : undefined,
                study_plan_id: task.study_plan_id || undefined,
              });
              await emitSignal(signal);
            } else {
              const signal = createTaskCompletedSignal(user.id, {
                task_id: task.id,
                task_type: task.task_type,
                subject: task.related_subject || undefined,
                topic: task.related_topic || undefined,
                estimated_minutes: task.estimated_minutes || undefined,
                actual_minutes: actualMinutes || task.actual_minutes || undefined,
                study_plan_id: task.study_plan_id || undefined,
                completion_notes: completionNotes,
              });
              await emitSignal(signal);
            }
          } else if (status === 'dismissed') {
            const signal = createActivitySkippedSignal(user.id, {
              task_id: task.id,
              task_type: task.task_type,
              subject: task.related_subject || undefined,
              topic: task.related_topic || undefined,
              reason: 'user_dismissed',
            });
            await emitSignal(signal);
          }
        } catch (signalError) {
          // Don't fail the task update if signal emission fails
          console.error('[TASK] Failed to emit learning signal:', signalError);
        }
      }

      return task;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: taskKeys.active(user.id) });
        // Use partial key to match all completed queries regardless of limit
        queryClient.invalidateQueries({ queryKey: ['tasks', 'completed', user.id] });
      }
    },
  });
}

/**
 * Complete a task
 */
export function useCompleteTask() {
  const updateStatus = useUpdateTaskStatus();

  return useMutation({
    mutationFn: async ({
      taskId,
      notes,
      minutes,
    }: {
      taskId: string;
      notes?: string;
      minutes?: number;
    }) => {
      return updateStatus.mutateAsync({
        taskId,
        status: 'completed',
        completionNotes: notes,
        actualMinutes: minutes,
      });
    },
  });
}

/**
 * Dismiss a task (can resurface later)
 */
export function useDismissTask() {
  const updateStatus = useUpdateTaskStatus();

  return useMutation({
    mutationFn: async (taskId: string) => {
      return updateStatus.mutateAsync({
        taskId,
        status: 'dismissed',
      });
    },
  });
}

/**
 * Start a task (set to in_progress)
 */
export function useStartTask() {
  const updateStatus = useUpdateTaskStatus();

  return useMutation({
    mutationFn: async (taskId: string) => {
      return updateStatus.mutateAsync({
        taskId,
        status: 'in_progress',
      });
    },
  });
}

/**
 * Restore a completed task back to pending status
 * Clears completion fields (completed_at, completion_notes, actual_minutes)
 */
export function useRestoreTask() {
  const updateStatus = useUpdateTaskStatus();

  return useMutation({
    mutationFn: async (taskId: string) => {
      return updateStatus.mutateAsync({
        taskId,
        status: 'pending',
      });
    },
  });
}

/**
 * Delete a task permanently
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (taskId: string): Promise<void> => {
      if (!user?.id) throw new Error('Not authenticated');

      // SECURITY: Always include user_id check to prevent deleting other users' tasks
      const { error } = await (supabase
        .from('user_tasks' as any)
        .delete()
        .eq('id', taskId)
        .eq('user_id', user.id) as unknown as Promise<{ error: any }>);

      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: taskKeys.active(user.id) });
        // Use partial key to match all completed queries regardless of limit
        queryClient.invalidateQueries({ queryKey: ['tasks', 'completed', user.id] });
      }
    },
  });
}

/**
 * Toggle task starred status
 */
export function useToggleTaskStar() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      taskId,
      isStarred,
    }: {
      taskId: string;
      isStarred: boolean;
    }): Promise<UserTask> => {
      if (!user?.id) throw new Error('Not authenticated');

      // SECURITY: Always include user_id check to prevent updating other users' tasks
      const { data, error } = await (supabase
        .from('user_tasks' as any)
        .update({ is_starred: isStarred })
        .eq('id', taskId)
        .eq('user_id', user.id)
        .select()
        .single() as unknown as Promise<{ data: UserTaskRow; error: any }>);

      if (error) throw error;
      return toTask(data);
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: taskKeys.active(user.id) });
        // Use partial key to match all completed queries regardless of limit
        queryClient.invalidateQueries({ queryKey: ['tasks', 'completed', user.id] });
      }
    },
  });
}

// ============================================================================
// TASK GENERATION
// ============================================================================

/**
 * Check if a diagnostic task already exists for a subject
 */
export async function hasDiagnosticTask(
  userId: string,
  subject: string
): Promise<boolean> {
  const { data, error } = await (supabase
    .from('user_tasks' as any)
    .select('id')
    .eq('user_id', userId)
    .eq('task_type', 'diagnostic')
    .eq('related_subject', subject)
    .in('status', ['pending', 'in_progress'])
    .limit(1) as unknown as Promise<{ data: { id: string }[] | null; error: any }>);

  if (error) {
    console.error('Error checking diagnostic task:', error);
    return false;
  }

  return (data?.length ?? 0) > 0;
}

/**
 * Generate diagnostic tasks for subjects that need assessment
 */
export function useGenerateDiagnosticTasks() {
  const createTask = useCreateTask();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (
      courses: Array<{ subject: string; label: string }>
    ): Promise<UserTask[]> => {
      if (!user?.id) throw new Error('Not authenticated');

      const createdTasks: UserTask[] = [];
      const STALE_DAYS = 21; // 3 weeks

      for (const course of courses) {
        // Check if task already exists
        const hasTask = await hasDiagnosticTask(user.id, course.subject);
        if (hasTask) continue;

        // Check last diagnostic for this subject
        const { data: lastSession } = await (supabase
          .from('diagnostic_sessions' as any)
          .select('created_at, score_percentage')
          .eq('user_id', user.id)
          .eq('subject', course.subject)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle() as unknown as Promise<{
          data: { created_at: string; score_percentage: number | null } | null;
          error: any;
        }>);

        const daysSinceDiagnostic = lastSession?.created_at
          ? differenceInDays(new Date(), parseISO(lastSession.created_at))
          : Infinity;

        // Only create task if diagnostic is stale or never done
        if (daysSinceDiagnostic >= STALE_DAYS) {
          const isNeverDone = daysSinceDiagnostic === Infinity;
          const priority = isNeverDone ? 3 : daysSinceDiagnostic >= 28 ? 2 : 1;

          const task = await createTask.mutateAsync({
            title: `${course.label} Diagnostic`,
            description: isNeverDone
              ? `You haven't assessed your ${course.label} knowledge yet. Take a diagnostic to personalize your learning.`
              : `It's been ${daysSinceDiagnostic} days since your last ${course.label} assessment. Retake to track your progress.`,
            task_type: 'diagnostic',
            priority,
            estimated_minutes: 10,
            related_subject: course.subject,
            source: 'auto_generated',
            metadata: {
              urgency_message: isNeverDone
                ? 'Never assessed'
                : `${daysSinceDiagnostic} days since last assessment`,
              weeks_since_diagnostic: Math.floor(daysSinceDiagnostic / 7),
              last_diagnostic_score: lastSession?.score_percentage ?? undefined,
              subject_label: course.label,
              action_url: '/diagnostic',
            },
          });

          createdTasks.push(task);
        }
      }

      return createdTasks;
    },
  });
}

// ============================================================================
// WEAKNESS TASK GENERATION
// ============================================================================

/**
 * Check if a weakness review task already exists
 */
export async function hasWeaknessTask(
  userId: string,
  category: string
): Promise<boolean> {
  const query = supabase
    .from('user_tasks' as any)
    .select('id')
    .eq('user_id', userId)
    .eq('task_type', 'review_topic')
    .eq('related_subject', category)
    .in('status', ['pending', 'in_progress'])
    .limit(1);

  // Optionally filter by subcategory in metadata
  const { data, error } = await (query as unknown as Promise<{ data: { id: string }[] | null; error: any }>);

  if (error) {
    console.error('Error checking weakness task:', error);
    return false;
  }

  return (data?.length ?? 0) > 0;
}

/**
 * Generate tasks for neglected weaknesses
 */
export function useGenerateWeaknessTasks() {
  const createTask = useCreateTask();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (
      weaknesses: Array<{
        id: string;
        category: string;
        subcategory?: string | null;
        confidence: number;
        last_practiced_at?: string | null;
      }>
    ): Promise<UserTask[]> => {
      if (!user?.id) throw new Error('Not authenticated');

      const createdTasks: UserTask[] = [];
      const STALE_DAYS = 14; // 2 weeks

      for (const weakness of weaknesses) {
        // Skip if not confident enough
        if (weakness.confidence < 0.5) continue;

        // Check staleness
        const daysSincePractice = weakness.last_practiced_at
          ? differenceInDays(new Date(), parseISO(weakness.last_practiced_at))
          : Infinity;

        if (daysSincePractice < STALE_DAYS) continue;

        // Check if task already exists
        const hasTask = await hasWeaknessTask(user.id, weakness.category);
        if (hasTask) continue;

        // Determine priority based on confidence and neglect
        let priority = 1; // normal
        if (weakness.confidence >= 0.8 || daysSincePractice >= 21) {
          priority = 2; // high
        }
        if (weakness.confidence >= 0.9 || daysSincePractice >= 28) {
          priority = 3; // urgent
        }

        const title = weakness.subcategory
          ? `Review ${weakness.category} / ${weakness.subcategory}`
          : `Review ${weakness.category}`;

        const description = daysSincePractice === Infinity
          ? `You've never practiced this topic. It's been flagged as a weakness.`
          : `It's been ${daysSincePractice} days since you practiced this topic.`;

        const task = await createTask.mutateAsync({
          title,
          description,
          task_type: 'review_topic',
          priority,
          estimated_minutes: 15,
          related_subject: weakness.category,
          related_topic: weakness.subcategory || undefined,
          source: 'auto_generated',
          metadata: {
            urgency_message: `Confidence: ${Math.round(weakness.confidence * 100)}%`,
            weakness_confidence: weakness.confidence,
            days_since_practice: daysSincePractice,
            belief_id: weakness.id,
            action_url: `/flashcards/study?subject=${encodeURIComponent(weakness.category.toLowerCase())}`,
          },
        });

        createdTasks.push(task);
      }

      return createdTasks;
    },
  });
}

// ============================================================================
// EXAM PREP TASK GENERATION
// ============================================================================

/**
 * Check if an exam prep task already exists
 */
export async function hasExamPrepTask(
  userId: string,
  courseName: string,
  examDate: string
): Promise<boolean> {
  const { data, error } = await (supabase
    .from('user_tasks' as any)
    .select('id')
    .eq('user_id', userId)
    .eq('task_type', 'review_topic')
    .eq('due_date', examDate)
    .ilike('title', `%${courseName}%`)
    .in('status', ['pending', 'in_progress'])
    .limit(1) as unknown as Promise<{ data: { id: string }[] | null; error: any }>);

  if (error) {
    console.error('Error checking exam prep task:', error);
    return false;
  }

  return (data?.length ?? 0) > 0;
}

/**
 * Generate exam prep tasks from syllabi
 */
export function useGenerateExamPrepTasks() {
  const createTask = useCreateTask();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (
      exams: Array<{
        courseName: string;
        examName: string;
        examDate: string;
        topicsCovered?: string[];
      }>
    ): Promise<UserTask[]> => {
      if (!user?.id) throw new Error('Not authenticated');

      const createdTasks: UserTask[] = [];
      const now = new Date();

      for (const exam of exams) {
        const examDate = parseISO(exam.examDate);
        const daysUntil = differenceInDays(examDate, now);

        // Only create tasks for exams within 14 days
        if (daysUntil < 0 || daysUntil > 14) continue;

        // Check if task already exists
        const hasTask = await hasExamPrepTask(user.id, exam.courseName, exam.examDate);
        if (hasTask) continue;

        // Determine priority based on proximity
        let priority = 1; // normal
        if (daysUntil <= 7) {
          priority = 2; // high
        }
        if (daysUntil <= 3) {
          priority = 3; // urgent
        }

        const title = `Prepare for ${exam.courseName} ${exam.examName}`;
        const topicsText = exam.topicsCovered?.length
          ? ` Topics: ${exam.topicsCovered.slice(0, 3).join(', ')}`
          : '';
        const description = `Exam in ${daysUntil} days.${topicsText}`;

        const task = await createTask.mutateAsync({
          title,
          description,
          task_type: 'review_topic',
          priority,
          due_date: exam.examDate,
          estimated_minutes: 60,
          related_subject: exam.courseName.toLowerCase().replace(/\s+/g, '_'),
          source: 'auto_generated',
          metadata: {
            urgency_message: `${daysUntil} days until exam`,
            days_until_exam: daysUntil,
            exam_name: exam.examName,
            topics_covered: exam.topicsCovered,
            action_url: `/flashcards/study?subject=${encodeURIComponent(exam.courseName.toLowerCase())}`,
          },
        });

        createdTasks.push(task);
      }

      return createdTasks;
    },
  });
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Check if user has any urgent tasks
 */
export function useHasUrgentTasks(): boolean {
  const { data: activeTasks = [] } = useActiveTasks();
  return activeTasks.some((task) => task.priority >= 3);
}

/**
 * Get task count by status
 */
export function useTaskCounts() {
  const { data: activeTasks = [], isLoading: activeLoading } = useActiveTasks();
  const { data: completedTasks = [], isLoading: completedLoading } =
    useCompletedTasks(100);

  return {
    pending: activeTasks.filter((t) => t.status === 'pending').length,
    inProgress: activeTasks.filter((t) => t.status === 'in_progress').length,
    completed: completedTasks.length,
    total: activeTasks.length + completedTasks.length,
    isLoading: activeLoading || completedLoading,
  };
}

// ============================================================================
// SYLLABUS TASK GENERATION
// ============================================================================

/**
 * Check if a task already exists for a specific syllabus item
 */
export async function hasSyllabusTask(
  userId: string,
  syllabusId: string,
  itemType: 'exam' | 'assignment',
  itemIndex: number
): Promise<boolean> {
  const { data, error } = await (supabase
    .from('user_tasks' as any)
    .select('id')
    .eq('user_id', userId)
    .eq('syllabus_id', syllabusId)
    .contains('metadata', { syllabus_item_type: itemType, syllabus_item_index: itemIndex })
    .limit(1) as unknown as Promise<{ data: { id: string }[] | null; error: any }>);

  if (error) {
    console.error('Error checking syllabus task:', error);
    return false;
  }

  return (data?.length ?? 0) > 0;
}

/**
 * Get all tasks for a syllabus
 */
export function useTasksBySyllabus(syllabusId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...taskKeys.all, 'syllabus', syllabusId],
    queryFn: async (): Promise<UserTask[]> => {
      if (!user?.id || !syllabusId) return [];

      const { data, error } = await (supabase
        .from('user_tasks' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('syllabus_id', syllabusId)
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('priority', { ascending: false }) as unknown as Promise<{
        data: UserTaskRow[] | null;
        error: any;
      }>);

      if (error) throw error;
      return (data ?? []).map(toTask);
    },
    enabled: !!user?.id && !!syllabusId,
  });
}

/**
 * Generate tasks from a parsed syllabus
 * Creates tasks for exams and assignments with due dates
 */
export function useGenerateTasksFromSyllabus() {
  const createTask = useCreateTask();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      syllabusId,
      courseName,
      exams,
      assignments,
    }: {
      syllabusId: string;
      courseName: string;
      exams: Array<{
        name: string;
        type: string;
        date?: string;
        topics_covered?: string[];
        weight?: number;
        duration_minutes?: number;
      }>;
      assignments: Array<{
        title: string;
        type: string;
        due_date?: string;
        weight?: number;
        description?: string;
      }>;
    }): Promise<{ examTasks: UserTask[]; assignmentTasks: UserTask[] }> => {
      if (!user?.id) throw new Error('Not authenticated');

      const examTasks: UserTask[] = [];
      const assignmentTasks: UserTask[] = [];
      const now = new Date();

      // Generate exam tasks
      for (let i = 0; i < exams.length; i++) {
        const exam = exams[i];
        if (!exam.date) continue;

        // Check if task already exists for this syllabus item
        const hasTask = await hasSyllabusTask(user.id, syllabusId, 'exam', i);
        if (hasTask) continue;

        const examDate = parseISO(exam.date);
        const daysUntil = differenceInDays(examDate, now);

        // Skip past exams
        if (daysUntil < -1) continue;

        // Determine priority based on proximity
        let priority = 1; // normal
        if (daysUntil <= 14) {
          priority = 2; // high
        }
        if (daysUntil <= 7) {
          priority = 3; // urgent
        }

        const title = `Prepare for ${courseName} ${exam.name}`;
        const topicsText = exam.topics_covered?.length
          ? ` Topics: ${exam.topics_covered.slice(0, 5).join(', ')}`
          : '';
        const weightText = exam.weight ? ` (${exam.weight}% of grade)` : '';
        const description = daysUntil >= 0
          ? `Exam in ${daysUntil} days.${weightText}${topicsText}`
          : `Exam was yesterday.${weightText}${topicsText}`;

        const task = await createTask.mutateAsync({
          title,
          description,
          task_type: 'practice',
          priority,
          due_date: exam.date,
          estimated_minutes: exam.duration_minutes || 60,
          related_subject: normalizeSubject(courseName) || courseName.toLowerCase().replace(/\s+/g, '_'),
          syllabus_id: syllabusId,
          source: 'syllabus',
          metadata: {
            urgency_message: daysUntil >= 0 ? `${daysUntil} days until exam` : 'Past exam',
            days_until_exam: daysUntil,
            exam_name: exam.name,
            exam_type: exam.type,
            exam_weight: exam.weight,
            topics_covered: exam.topics_covered,
            syllabus_item_type: 'exam',
            syllabus_item_index: i,
            action_url: `/flashcards/study?subject=${encodeURIComponent(normalizeSubject(courseName) || courseName.toLowerCase())}`,
          },
        });

        examTasks.push(task);
      }

      // Generate assignment tasks
      for (let i = 0; i < assignments.length; i++) {
        const assignment = assignments[i];
        if (!assignment.due_date) continue;

        // Check if task already exists for this syllabus item
        const hasTask = await hasSyllabusTask(user.id, syllabusId, 'assignment', i);
        if (hasTask) continue;

        const dueDate = parseISO(assignment.due_date);
        const daysUntil = differenceInDays(dueDate, now);

        // Skip past assignments
        if (daysUntil < -1) continue;

        // Determine priority based on proximity
        let priority = 1; // normal
        if (daysUntil <= 7) {
          priority = 2; // high
        }
        if (daysUntil <= 3) {
          priority = 3; // urgent
        }

        // Map assignment type to task type
        let taskType: TaskType = 'other';
        switch (assignment.type) {
          case 'essay':
            taskType = 'essay';
            break;
          case 'quiz':
          case 'exam':
            taskType = 'practice';
            break;
          case 'project':
            taskType = 'other';
            break;
          default:
            taskType = 'reading';
        }

        const title = `${assignment.title} (${courseName})`;
        const weightText = assignment.weight ? ` - ${assignment.weight}% of grade` : '';
        const description = assignment.description
          ? `${assignment.description}${weightText}`
          : `Due in ${daysUntil} days.${weightText}`;

        const task = await createTask.mutateAsync({
          title,
          description,
          task_type: taskType,
          priority,
          due_date: assignment.due_date,
          estimated_minutes: taskType === 'essay' ? 120 : 60,
          related_subject: normalizeSubject(courseName) || courseName.toLowerCase().replace(/\s+/g, '_'),
          syllabus_id: syllabusId,
          source: 'syllabus',
          metadata: {
            urgency_message: daysUntil >= 0 ? `Due in ${daysUntil} days` : 'Past due',
            days_until_due: daysUntil,
            assignment_type: assignment.type,
            assignment_weight: assignment.weight,
            syllabus_item_type: 'assignment',
            syllabus_item_index: i,
            action_url: taskType === 'essay' ? '/essay-coach' : `/flashcards/study?subject=${encodeURIComponent(normalizeSubject(courseName) || courseName.toLowerCase())}`,
          },
        });

        assignmentTasks.push(task);
      }

      // Emit intelligence signal if any tasks were created
      const totalTasks = examTasks.length + assignmentTasks.length;
      if (totalTasks > 0) {
        try {
          // Collect unique subjects from created tasks
          const subjects = new Set<string>();
          [...examTasks, ...assignmentTasks].forEach(task => {
            if (task.related_subject) subjects.add(task.related_subject);
          });

          // Collect upcoming exam dates
          const examDates = examTasks
            .filter(task => task.due_date)
            .map(task => task.due_date!)
            .sort();

          const signal = createSyllabusTasksGeneratedSignal(user.id, {
            syllabus_id: syllabusId,
            course_name: courseName,
            exam_tasks_created: examTasks.length,
            assignment_tasks_created: assignmentTasks.length,
            subjects_covered: Array.from(subjects),
            upcoming_exam_dates: examDates.length > 0 ? examDates : undefined,
          });
          await emitSignal(signal);
        } catch (signalError) {
          // Don't fail task generation if signal emission fails
          console.error('[SYLLABUS] Failed to emit learning signal:', signalError);
        }
      }

      return { examTasks, assignmentTasks };
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: taskKeys.active(user.id) });
      }
    },
  });
}

/**
 * Delete all tasks from a syllabus
 */
export function useDeleteSyllabusTasks() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (syllabusId: string): Promise<number> => {
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await (supabase
        .from('user_tasks' as any)
        .delete()
        .eq('user_id', user.id)
        .eq('syllabus_id', syllabusId)
        .select('id') as unknown as Promise<{ data: { id: string }[] | null; error: any }>);

      if (error) throw error;
      return data?.length ?? 0;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: taskKeys.active(user.id) });
        // Use partial key to match all completed queries regardless of limit
        queryClient.invalidateQueries({ queryKey: ['tasks', 'completed', user.id] });
      }
    },
  });
}
